import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  Bell,
  ChevronLeft,
  ChevronRight,
  Clapperboard,
  Database,
  Download,
  LogOut,
  PackageOpen,
  RotateCcw,
  Search,
  Save,
  Shield,
  Store,
  Trash2,
  Upload,
  UserPlus,
} from 'lucide-react';
import apiClient from '../services/apiClient';
import { SUPPORTED_LANGUAGES } from '../i18n';
import { useConfirmDialog } from './ConfirmDialog';
import AdminMarketPanel from './AdminMarketPanel';
import AdminSceneSelectionPanel from './AdminSceneSelectionPanel';
import './AdminPage.css';

const STATUS_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'verified', label: 'Verified' },
  { value: 'unverified', label: 'Unverified' },
  { value: 'staleUnverified', label: 'Unverified over 1 week' },
];

const emptyCreateForm = {
  userId: '',
  password: '',
  nickname: '',
  language: 'en',
  membershipPlan: 'user',
};

function formatDate(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('en-US', {
    year: '2-digit',
    month: '2-digit',
    day: '2-digit',
  });
}

function formatDateTime(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('en-US');
}

function formatBytes(value) {
  const bytes = Number(value) || 0;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function ReleaseSummary({ release, emptyLabel }) {
  if (!release) return <p className="admin-release-empty">{emptyLabel}</p>;
  return (
    <div className="admin-release-summary">
      <strong>{release.releaseId}</strong>
      <span className={`admin-release-status is-${release.status}`}>{release.status}</span>
      <small>{formatDateTime(release.publishedAt || release.createdAt)}</small>
      {release.summary && (
        <small>
          {release.summary.locales} locales · {release.summary.presets !== undefined
            ? `${release.summary.presets} presets · ${release.summary.cards} cards`
            : `${release.summary.scenes} scenes · ${release.summary.triggers} triggers`}
        </small>
      )}
      {release.summary?.scenes !== undefined && (
        <small>{release.assetsReady ? 'Images ready' : 'Images require a new Publish'}</small>
      )}
    </div>
  );
}

function AdminPage() {
  const [adminToken, setAdminToken] = useState(apiClient.getAdminToken());
  const [loginForm, setLoginForm] = useState({ id: '', password: '' });
  const [createForm, setCreateForm] = useState(emptyCreateForm);
  const [users, setUsers] = useState([]);
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('all');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoginLoading, setIsLoginLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [updatingMembershipId, setUpdatingMembershipId] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [activeTab, setActiveTab] = useState('users');
  const [presetStatus, setPresetStatus] = useState({ activeRelease: null, previousRelease: null });
  const [presetBundle, setPresetBundle] = useState(null);
  const [validation, setValidation] = useState(null);
  const [publishConfirmation, setPublishConfirmation] = useState('');
  const [isPresetLoading, setIsPresetLoading] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [isNotificationLoading, setIsNotificationLoading] = useState(false);
  const [savingNotificationLocale, setSavingNotificationLocale] = useState('');
  const [sceneStatus, setSceneStatus] = useState({ activeRelease: null, previousRelease: null, legacy: true });
  const [sceneBundle, setSceneBundle] = useState(null);
  const [sceneValidation, setSceneValidation] = useState(null);
  const [scenePublishConfirmation, setScenePublishConfirmation] = useState('');
  const [sceneResult, setSceneResult] = useState({ scenes: [], total: 0, page: 1, totalPages: 1, triggers: [] });
  const [sceneFilters, setSceneFilters] = useState({ query: '', locale: '', trigger: '', page: 1 });
  const [isSceneLoading, setIsSceneLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState('');
  const { confirm, confirmDialog } = useConfirmDialog();

  const userCountLabel = useMemo(() => `${users.length} user${users.length === 1 ? '' : 's'}`, [users.length]);

  const loadUsers = useCallback(async () => {
    if (!apiClient.getAdminToken()) return;
    setIsLoading(true);
    setError('');
    try {
      const result = await apiClient.getAdminUsers({ query, status });
      setUsers(Array.isArray(result?.users) ? result.users : []);
    } catch (err) {
      if (err.status === 401) {
        apiClient.clearAdminToken();
        setAdminToken('');
      }
      setError(err.message || 'Failed to load users');
    } finally {
      setIsLoading(false);
    }
  }, [query, status]);

  const handleAdminError = useCallback((err, fallback) => {
    if (err.status === 401) {
      apiClient.clearAdminToken();
      setAdminToken('');
    }
    setError(err.message || fallback);
  }, []);

  const handleMarketError = useCallback((err) => {
    handleAdminError(err, 'Failed to load Market moderation data');
  }, [handleAdminError]);

  const handleMarketMessage = useCallback((value) => {
    setError('');
    setMessage(value);
  }, []);

  const loadPresetStatus = useCallback(async () => {
    if (!apiClient.getAdminToken()) return;
    setIsPresetLoading(true);
    setError('');
    try {
      const result = await apiClient.getAdminPresetLibraryStatus();
      setPresetStatus(result || { activeRelease: null, previousRelease: null });
    } catch (err) {
      handleAdminError(err, 'Failed to load Preset Library status');
    } finally {
      setIsPresetLoading(false);
    }
  }, [handleAdminError]);

  const loadNotifications = useCallback(async () => {
    if (!apiClient.getAdminToken()) return;
    setIsNotificationLoading(true);
    setError('');
    try {
      const result = await apiClient.getAdminNotifications();
      setNotifications(Array.isArray(result?.notifications) ? result.notifications : []);
    } catch (err) {
      handleAdminError(err, 'Failed to load notifications');
    } finally {
      setIsNotificationLoading(false);
    }
  }, [handleAdminError]);

  const loadScenes = useCallback(async () => {
    if (!apiClient.getAdminToken()) return;
    setIsSceneLoading(true);
    setError('');
    try {
      const [statusResult, listResult] = await Promise.all([
        apiClient.getAdminSceneLibraryStatus(),
        apiClient.getAdminScenes({ ...sceneFilters, pageSize: 25 }),
      ]);
      setSceneStatus(statusResult || { activeRelease: null, previousRelease: null, legacy: true });
      setSceneResult(listResult || { scenes: [], total: 0, page: 1, totalPages: 1, triggers: [] });
    } catch (err) {
      handleAdminError(err, 'Failed to load scenes');
    } finally {
      setIsSceneLoading(false);
    }
  }, [handleAdminError, sceneFilters]);

  useEffect(() => {
    if (!adminToken || activeTab !== 'users') return undefined;
    const timeoutId = setTimeout(() => {
      loadUsers();
    }, 180);
    return () => clearTimeout(timeoutId);
  }, [activeTab, adminToken, loadUsers]);

  useEffect(() => {
    if (!adminToken || activeTab !== 'presets') return;
    loadPresetStatus();
  }, [activeTab, adminToken, loadPresetStatus]);

  useEffect(() => {
    if (!adminToken || activeTab !== 'notifications') return;
    loadNotifications();
  }, [activeTab, adminToken, loadNotifications]);

  useEffect(() => {
    if (!adminToken || activeTab !== 'scenes') return undefined;
    const timeoutId = setTimeout(loadScenes, 180);
    return () => clearTimeout(timeoutId);
  }, [activeTab, adminToken, loadScenes]);

  const handleLogin = async (event) => {
    event.preventDefault();
    setIsLoginLoading(true);
    setError('');
    setMessage('');
    try {
      await apiClient.adminLogin(loginForm);
      setAdminToken(apiClient.getAdminToken());
      setLoginForm({ id: '', password: '' });
    } catch (err) {
      setError(err.message || 'Admin login failed');
    } finally {
      setIsLoginLoading(false);
    }
  };

  const handleLogout = () => {
    apiClient.clearAdminToken();
    setAdminToken('');
    setUsers([]);
    setActiveTab('users');
    setPresetStatus({ activeRelease: null, previousRelease: null });
    setPresetBundle(null);
    setValidation(null);
    setPublishConfirmation('');
    setNotifications([]);
    setSceneStatus({ activeRelease: null, previousRelease: null, legacy: true });
    setSceneResult({ scenes: [], total: 0, page: 1, totalPages: 1, triggers: [] });
    setSceneValidation(null);
    setMessage('');
    setError('');
  };

  const handleCreateUser = async (event) => {
    event.preventDefault();
    setIsCreating(true);
    setError('');
    setMessage('');
    try {
      await apiClient.createAdminUser(createForm);
      setCreateForm(emptyCreateForm);
      setMessage('Special user created.');
      await loadUsers();
    } catch (err) {
      setError(err.message || 'Failed to create user');
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteUser = async (user) => {
    const accepted = await confirm({
      title: 'Delete User?',
      message: `Delete "${user.userId}" and all owned cards, drills, sessions, and tokens?`,
      confirmLabel: 'Delete',
      cancelLabel: 'Keep',
      tone: 'danger',
    });
    if (!accepted) return;

    setError('');
    setMessage('');
    try {
      await apiClient.deleteAdminUser(user.userId);
      setMessage(`Deleted ${user.userId}.`);
      await loadUsers();
    } catch (err) {
      setError(err.message || 'Failed to delete user');
    }
  };

  const handleMembershipChange = async (user, membershipPlan) => {
    setUpdatingMembershipId(user.userId);
    setError('');
    setMessage('');
    try {
      const result = await apiClient.updateAdminUserMembership(user.userId, membershipPlan);
      setUsers((current) => current.map((item) => (
        item.userId === user.userId ? result.user : item
      )));
      setMessage(`Updated ${user.userId} to ${membershipPlan}.`);
    } catch (err) {
      setError(err.message || 'Failed to update membership');
    } finally {
      setUpdatingMembershipId('');
    }
  };

  const handleValidatePresetBundle = async (event) => {
    event.preventDefault();
    if (!presetBundle) return;
    setIsPresetLoading(true);
    setError('');
    setMessage('');
    setValidation(null);
    setPublishConfirmation('');
    try {
      const result = await apiClient.validateAdminPresetLibrary(presetBundle);
      setValidation(result);
      setMessage(result.unchanged
        ? 'This bundle is identical to the active release.'
        : 'Bundle validation succeeded. Review the changes before publishing.');
      await loadPresetStatus();
    } catch (err) {
      setValidation({
        releaseId: err.data?.error?.releaseId || null,
        errors: err.data?.error?.errors || [],
        warnings: err.data?.error?.warnings || [],
      });
      handleAdminError(err, 'Preset Library validation failed');
    } finally {
      setIsPresetLoading(false);
    }
  };

  const handlePublishPresetBundle = async () => {
    if (!validation?.releaseId || validation.unchanged) return;
    const accepted = await confirm({
      title: 'Publish Preset Library?',
      message: 'This makes the staged release visible immediately. Confirmation: PUBLISH PRESET LIBRARY',
      confirmLabel: 'Publish',
      cancelLabel: 'Cancel',
      tone: 'danger',
    });
    if (!accepted) return;
    setIsPresetLoading(true);
    setError('');
    setMessage('');
    try {
      await apiClient.publishAdminPresetLibrary({
        releaseId: validation.releaseId,
        expectedActiveReleaseId: validation.expectedActiveReleaseId,
        confirmation: publishConfirmation,
      });
      setValidation(null);
      setPublishConfirmation('');
      setPresetBundle(null);
      setMessage('Preset Library published successfully.');
      await loadPresetStatus();
    } catch (err) {
      handleAdminError(err, 'Preset Library publish failed');
    } finally {
      setIsPresetLoading(false);
    }
  };

  const handleRollbackPresetLibrary = async () => {
    if (!presetStatus.previousRelease) return;
    const accepted = await confirm({
      title: 'Rollback Preset Library?',
      message: `Activate previous release ${presetStatus.previousRelease.releaseId}? Confirmation: ROLLBACK PRESET LIBRARY`,
      confirmLabel: 'Rollback',
      cancelLabel: 'Cancel',
      tone: 'danger',
    });
    if (!accepted) return;
    setIsPresetLoading(true);
    setError('');
    setMessage('');
    try {
      await apiClient.rollbackAdminPresetLibrary({ confirmation: 'ROLLBACK PRESET LIBRARY' });
      setValidation(null);
      setMessage('Preset Library rollback completed.');
      await loadPresetStatus();
    } catch (err) {
      handleAdminError(err, 'Preset Library rollback failed');
    } finally {
      setIsPresetLoading(false);
    }
  };

  const downloadArchive = async (kind) => {
    setIsDownloading(kind);
    setError('');
    try {
      const result = kind === 'presets'
        ? await apiClient.exportAdminPresetLibrary()
        : await apiClient.exportAdminSceneLibrary();
      const url = URL.createObjectURL(result.blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = result.filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
      setMessage(`${kind === 'presets' ? 'Preset' : 'Scene'} ZIP downloaded.`);
    } catch (err) {
      handleAdminError(err, `Failed to export ${kind}`);
    } finally {
      setIsDownloading('');
    }
  };

  const updateNotificationDraft = (locale, field, value) => {
    setNotifications((current) => current.map((notification) => (
      notification.locale === locale ? { ...notification, [field]: value } : notification
    )));
  };

  const saveNotification = async (notification) => {
    setSavingNotificationLocale(notification.locale);
    setError('');
    setMessage('');
    try {
      const result = await apiClient.updateAdminNotification(notification.locale, {
        title: notification.title,
        message: notification.message,
        active: notification.active,
      });
      setNotifications((current) => current.map((item) => (
        item.locale === notification.locale ? { ...item, ...result.notification } : item
      )));
      setMessage(`${notification.label || notification.locale} notification saved.`);
    } catch (err) {
      handleAdminError(err, 'Failed to save notification');
    } finally {
      setSavingNotificationLocale('');
    }
  };

  const handleValidateSceneBundle = async (event) => {
    event.preventDefault();
    if (!sceneBundle) return;
    setIsSceneLoading(true);
    setError('');
    setMessage('');
    setSceneValidation(null);
    setScenePublishConfirmation('');
    try {
      const result = await apiClient.validateAdminSceneLibrary(sceneBundle);
      setSceneValidation(result);
      setMessage(result.unchanged ? 'This bundle is identical to the active scene release.' : 'Scene ZIP validation succeeded.');
      const statusResult = await apiClient.getAdminSceneLibraryStatus();
      setSceneStatus(statusResult);
    } catch (err) {
      setSceneValidation({
        releaseId: err.data?.error?.releaseId || null,
        errors: err.data?.error?.errors || [],
        warnings: err.data?.error?.warnings || [],
      });
      handleAdminError(err, 'Scene ZIP validation failed');
    } finally {
      setIsSceneLoading(false);
    }
  };

  const handlePublishSceneBundle = async () => {
    if (!sceneValidation?.releaseId || sceneValidation.unchanged) return;
    const accepted = await confirm({
      title: 'Publish Scene Library?',
      message: 'This makes the staged scenes active immediately.',
      confirmLabel: 'Publish',
      cancelLabel: 'Cancel',
      tone: 'danger',
    });
    if (!accepted) return;
    setIsSceneLoading(true);
    setError('');
    try {
      await apiClient.publishAdminSceneLibrary({
        releaseId: sceneValidation.releaseId,
        expectedActiveReleaseId: sceneValidation.expectedActiveReleaseId,
        confirmation: scenePublishConfirmation,
      });
      setSceneValidation(null);
      setSceneBundle(null);
      setScenePublishConfirmation('');
      setMessage('Scene Library published successfully.');
      await loadScenes();
    } catch (err) {
      handleAdminError(err, 'Scene Library publish failed');
    } finally {
      setIsSceneLoading(false);
    }
  };

  const handleRollbackScenes = async () => {
    if (!sceneStatus.previousRelease) return;
    const accepted = await confirm({
      title: 'Rollback Scene Library?',
      message: `Activate previous release ${sceneStatus.previousRelease.releaseId}?`,
      confirmLabel: 'Rollback',
      cancelLabel: 'Cancel',
      tone: 'danger',
    });
    if (!accepted) return;
    setIsSceneLoading(true);
    try {
      await apiClient.rollbackAdminSceneLibrary({ confirmation: 'ROLLBACK SCENE LIBRARY' });
      setSceneValidation(null);
      setMessage('Scene Library rollback completed.');
      await loadScenes();
    } catch (err) {
      handleAdminError(err, 'Scene Library rollback failed');
    } finally {
      setIsSceneLoading(false);
    }
  };

  if (!adminToken) {
    return (
      <div className="admin-page">
        <section className="admin-login-card">
          <div className="admin-brand">
            <Shield size={34} strokeWidth={2.3} aria-hidden="true" />
            <div>
              <h1>Admin Management</h1>
              <p>Master access only</p>
            </div>
          </div>
          <form className="admin-login-form" onSubmit={handleLogin}>
            <label htmlFor="admin-id">Admin ID</label>
            <input
              id="admin-id"
              value={loginForm.id}
              onChange={(event) => setLoginForm((current) => ({ ...current, id: event.target.value }))}
              disabled={isLoginLoading}
              autoComplete="username"
              required
            />
            <label htmlFor="admin-password">Password</label>
            <input
              id="admin-password"
              type="password"
              value={loginForm.password}
              onChange={(event) => setLoginForm((current) => ({ ...current, password: event.target.value }))}
              disabled={isLoginLoading}
              autoComplete="current-password"
              required
            />
            {error && <div className="admin-error">{error}</div>}
            <button type="submit" disabled={isLoginLoading}>
              {isLoginLoading ? 'Checking...' : 'Enter Admin'}
            </button>
          </form>
        </section>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <header className="admin-header">
        <div>
          <h1>Admin Management</h1>
          <p>Users, presets, notifications, scenes, and Market moderation</p>
        </div>
        <button type="button" className="admin-logout" onClick={handleLogout}>
          <LogOut size={17} strokeWidth={2.3} aria-hidden="true" />
          <span>Logout</span>
        </button>
      </header>

      <nav className="admin-tabs" aria-label="Admin sections">
        <button
          type="button"
          className={activeTab === 'users' ? 'is-active' : ''}
          onClick={() => setActiveTab('users')}
        >
          <UserPlus size={17} aria-hidden="true" /> Users
        </button>
        <button
          type="button"
          className={activeTab === 'presets' ? 'is-active' : ''}
          onClick={() => setActiveTab('presets')}
        >
          <PackageOpen size={17} aria-hidden="true" /> Presets
        </button>
        <button
          type="button"
          className={activeTab === 'notifications' ? 'is-active' : ''}
          onClick={() => setActiveTab('notifications')}
        >
          <Bell size={17} aria-hidden="true" /> Notifications
        </button>
        <button
          type="button"
          className={activeTab === 'scenes' ? 'is-active' : ''}
          onClick={() => setActiveTab('scenes')}
        >
          <Clapperboard size={17} aria-hidden="true" /> Scenes
        </button>
        <button
          type="button"
          className={activeTab === 'market' ? 'is-active' : ''}
          onClick={() => setActiveTab('market')}
        >
          <Store size={17} aria-hidden="true" /> Market
        </button>
      </nav>

      {(error || message) && (
        <div className={error ? 'admin-error' : 'admin-message'}>
          {error || message}
        </div>
      )}

      {activeTab === 'users' && <>
      <section className="admin-panel admin-create-panel">
        <div className="admin-panel-title">
          <UserPlus size={22} strokeWidth={2.2} aria-hidden="true" />
          <h2>Add Special User</h2>
        </div>
        <form className="admin-create-form" onSubmit={handleCreateUser}>
          <input
            value={createForm.userId}
            onChange={(event) => setCreateForm((current) => ({ ...current, userId: event.target.value }))}
            placeholder="ID"
            disabled={isCreating}
            required
          />
          <input
            type="password"
            value={createForm.password}
            onChange={(event) => setCreateForm((current) => ({ ...current, password: event.target.value }))}
            placeholder="Password"
            disabled={isCreating}
            required
          />
          <input
            value={createForm.nickname}
            onChange={(event) => setCreateForm((current) => ({ ...current, nickname: event.target.value }))}
            placeholder="Nickname"
            disabled={isCreating}
            required
          />
          <select
            value={createForm.language}
            onChange={(event) => setCreateForm((current) => ({ ...current, language: event.target.value }))}
            disabled={isCreating}
          >
            {SUPPORTED_LANGUAGES.map((language) => (
              <option key={language.code} value={language.code}>{language.label}</option>
            ))}
          </select>
          <select
            value={createForm.membershipPlan}
            onChange={(event) => setCreateForm((current) => ({ ...current, membershipPlan: event.target.value }))}
            disabled={isCreating}
            aria-label="Membership plan"
          >
            <option value="user">User</option>
            <option value="friend">Friend</option>
            <option value="membership">Membership</option>
          </select>
          <button type="submit" disabled={isCreating}>
            {isCreating ? 'Adding...' : 'Add User'}
          </button>
        </form>
      </section>

      <section className="admin-panel">
        <div className="admin-toolbar">
          <div className="admin-panel-title">
            <Search size={22} strokeWidth={2.2} aria-hidden="true" />
            <h2>Users</h2>
            <span>{userCountLabel}</span>
          </div>
          <div className="admin-filters">
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search ID, nickname, email"
            />
            <select value={status} onChange={(event) => setStatus(event.target.value)}>
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
            <button type="button" onClick={loadUsers} disabled={isLoading}>
              {isLoading ? 'Loading...' : 'Refresh'}
            </button>
          </div>
        </div>

        <div className="admin-table-wrap">
          <table className="admin-user-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Nickname</th>
                <th>Language</th>
                <th>Level</th>
                <th>Membership</th>
                <th>Email Verification</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.userId} className={user.staleUnverified ? 'is-stale-unverified' : ''}>
                  <td>{user.userId}</td>
                  <td>{user.nickname}</td>
                  <td>{user.language}</td>
                  <td>Lv. {user.level}</td>
                  <td>
                    <select
                      className="admin-membership-select"
                      value={user.membershipPlan || 'membership'}
                      disabled={updatingMembershipId === user.userId}
                      onChange={(event) => handleMembershipChange(user, event.target.value)}
                      aria-label={`Membership plan for ${user.userId}`}
                    >
                      <option value="user">User</option>
                      <option value="friend">Friend</option>
                      <option value="membership">Membership</option>
                    </select>
                    <small>{user.membershipStatus === 'trial' ? 'Trial active' : user.membershipStatus}</small>
                  </td>
                  <td>
                    <span className={`admin-status-pill ${user.emailVerified ? 'is-verified' : 'is-unverified'}`}>
                      {user.emailVerified ? 'Verified' : user.staleUnverified ? 'Over 1 week' : 'Pending'}
                    </span>
                    {user.email && <small>{user.email}</small>}
                  </td>
                  <td>{formatDate(user.createdAt)}</td>
                  <td>
                    <button type="button" className="admin-delete-user" onClick={() => handleDeleteUser(user)}>
                      <Trash2 size={16} strokeWidth={2.3} aria-hidden="true" />
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
              {!isLoading && users.length === 0 && (
                <tr>
                  <td colSpan="8" className="admin-empty">No users found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
      </>}

      {activeTab === 'presets' && (
        <>
          <section className="admin-panel"><a href="/admin/presets">Preset 둘러보기 — 그룹별 목록 확인</a></section>
          <section className="admin-panel admin-release-grid">
            <div>
              <div className="admin-panel-title">
                <Database size={22} strokeWidth={2.2} aria-hidden="true" />
                <h2>Active Release</h2>
              </div>
              <ReleaseSummary release={presetStatus.activeRelease} emptyLabel="No Preset Library has been published." />
              <button
                type="button"
                className="admin-secondary-action"
                disabled={!presetStatus.activeRelease || isDownloading === 'presets'}
                onClick={() => downloadArchive('presets')}
              >
                <Download size={16} aria-hidden="true" /> {isDownloading === 'presets' ? 'Preparing...' : 'Export ZIP'}
              </button>
            </div>
            <div>
              <div className="admin-panel-title">
                <RotateCcw size={22} strokeWidth={2.2} aria-hidden="true" />
                <h2>Previous Release</h2>
              </div>
              <ReleaseSummary release={presetStatus.previousRelease} emptyLabel="No rollback release is available." />
              <button
                type="button"
                className="admin-secondary-action"
                disabled={!presetStatus.previousRelease || isPresetLoading}
                onClick={handleRollbackPresetLibrary}
              >
                <RotateCcw size={16} aria-hidden="true" /> Rollback
              </button>
            </div>
          </section>

          <section className="admin-panel">
            <div className="admin-panel-title">
              <Upload size={22} strokeWidth={2.2} aria-hidden="true" />
              <h2>Validate Library ZIP</h2>
            </div>
            <p className="admin-panel-help">
              Upload a bundle produced by <code>npm run presets:bundle -- &lt;library-folder&gt;</code>. Validation stages a release but does not change the active Library.
            </p>
            <form className="admin-preset-upload" onSubmit={handleValidatePresetBundle}>
              <input
                type="file"
                accept=".zip,application/zip"
                aria-label="Preset Library ZIP"
                onChange={(event) => {
                  setPresetBundle(event.target.files?.[0] || null);
                  setValidation(null);
                  setPublishConfirmation('');
                }}
                disabled={isPresetLoading}
              />
              <button type="submit" disabled={!presetBundle || isPresetLoading}>
                {isPresetLoading ? 'Working...' : 'Validate ZIP'}
              </button>
              <button type="button" className="admin-secondary-action" onClick={loadPresetStatus} disabled={isPresetLoading}>
                Refresh status
              </button>
            </form>
          </section>

          {validation && (
            <section className="admin-panel admin-validation-panel">
              <div className="admin-panel-title">
                <AlertTriangle size={22} strokeWidth={2.2} aria-hidden="true" />
                <h2>Validation Result</h2>
              </div>
              {validation.releaseId && <p><strong>Release:</strong> {validation.releaseId}</p>}
              {validation.summary && (
                <div className="admin-stat-grid">
                  <span><strong>{validation.summary.locales}</strong> locales</span>
                  <span><strong>{validation.summary.presets}</strong> presets</span>
                  <span><strong>{validation.summary.cards}</strong> cards</span>
                  {validation.summary.writingExpressions !== undefined && <span><strong>{validation.summary.writingExpressions}</strong> writing expressions</span>}
                  <span><strong>{validation.summary.ttsSources}</strong> TTS sources</span>
                </div>
              )}
              {validation.changes && (
                <div className="admin-change-grid">
                  <div><strong>Added IDs</strong><small>{validation.changes.addedPresetIds?.join(', ') || 'None'}</small></div>
                  <div><strong>Removed IDs</strong><small>{validation.changes.removedPresetIds?.join(', ') || 'None'}</small></div>
                  <div><strong>Retained IDs</strong><small>{validation.changes.retainedPresetIds?.join(', ') || 'None'}</small></div>
                </div>
              )}
              {!!validation.warnings?.length && (
                <div className="admin-validation-list is-warning">
                  <strong>Warnings</strong>
                  {validation.warnings.map((warning, index) => (
                    <small key={`${warning.path}-${warning.line || 0}-${index}`}>
                      {warning.path}{warning.line ? `:${warning.line}` : ''} — {warning.message}
                    </small>
                  ))}
                </div>
              )}
              {!!validation.errors?.length && (
                <div className="admin-validation-list is-error">
                  <strong>Errors</strong>
                  {validation.errors.map((item, index) => (
                    <small key={`${item.path || item.code}-${index}`}>
                      {item.path ? `${item.path} — ` : ''}{item.message}
                    </small>
                  ))}
                </div>
              )}
              {validation.releaseId && !validation.unchanged && !validation.errors?.length && (
                <div className="admin-publish-confirmation">
                  <label htmlFor="admin-publish-confirmation">Type <strong>PUBLISH PRESET LIBRARY</strong> to confirm</label>
                  <input
                    id="admin-publish-confirmation"
                    value={publishConfirmation}
                    onChange={(event) => setPublishConfirmation(event.target.value)}
                    autoComplete="off"
                    disabled={isPresetLoading}
                  />
                  <button
                    type="button"
                    className="admin-publish-action"
                    onClick={handlePublishPresetBundle}
                    disabled={isPresetLoading || publishConfirmation !== 'PUBLISH PRESET LIBRARY'}
                  >
                    Publish Preset Library
                  </button>
                </div>
              )}
            </section>
          )}
        </>
      )}

      {activeTab === 'notifications' && (
        <section className="admin-panel">
          <div className="admin-toolbar">
            <div className="admin-panel-title">
              <Bell size={22} strokeWidth={2.2} aria-hidden="true" />
              <h2>Notifications</h2>
            </div>
            <button type="button" className="admin-secondary-action" onClick={loadNotifications} disabled={isNotificationLoading}>
              {isNotificationLoading ? 'Loading...' : 'Refresh'}
            </button>
          </div>
          <p className="admin-panel-help">One current notice per language. Changing content or reactivating a notice creates a new revision for users who dismissed the previous one.</p>
          <div className="admin-notification-grid">
            {notifications.map((notification) => (
              <article className="admin-notification-editor" key={notification.locale}>
                <div className="admin-notification-heading">
                  <strong>{notification.label || notification.locale}</strong>
                  <label className="admin-active-toggle">
                    <input
                      type="checkbox"
                      checked={notification.active}
                      onChange={(event) => updateNotificationDraft(notification.locale, 'active', event.target.checked)}
                    />
                    Active
                  </label>
                </div>
                <label htmlFor={`notification-title-${notification.locale}`}>Title</label>
                <input
                  id={`notification-title-${notification.locale}`}
                  value={notification.title}
                  onChange={(event) => updateNotificationDraft(notification.locale, 'title', event.target.value)}
                />
                <label htmlFor={`notification-message-${notification.locale}`}>Message</label>
                <textarea
                  id={`notification-message-${notification.locale}`}
                  value={notification.message}
                  onChange={(event) => updateNotificationDraft(notification.locale, 'message', event.target.value)}
                  rows="6"
                />
                <small>Published: {formatDateTime(notification.publishedAt)}</small>
                <button
                  type="button"
                  className="admin-publish-action"
                  onClick={() => saveNotification(notification)}
                  disabled={savingNotificationLocale === notification.locale}
                >
                  <Save size={16} aria-hidden="true" /> {savingNotificationLocale === notification.locale ? 'Saving...' : 'Save'}
                </button>
              </article>
            ))}
          </div>
        </section>
      )}

      {activeTab === 'scenes' && (
        <>
          <section className="admin-panel"><a href="/admin/scenes">Scene 둘러보기 — 화면과 음성으로 확인</a></section>
          <AdminSceneSelectionPanel />
          <section className="admin-panel admin-release-grid">
            <div>
              <div className="admin-panel-title">
                <Clapperboard size={22} strokeWidth={2.2} aria-hidden="true" />
                <h2>Active Scene Release</h2>
              </div>
              {sceneStatus.legacy
                ? <p className="admin-release-empty">Legacy scenes are active. They will be captured automatically when a valid ZIP is staged.</p>
                : <ReleaseSummary release={sceneStatus.activeRelease} emptyLabel="No Scene Library has been published." />}
              <button
                type="button"
                className="admin-secondary-action"
                disabled={sceneResult.total === 0 || isDownloading === 'scenes'}
                onClick={() => downloadArchive('scenes')}
              >
                <Download size={16} aria-hidden="true" /> {isDownloading === 'scenes' ? 'Preparing...' : 'Export ZIP'}
              </button>
            </div>
            <div>
              <div className="admin-panel-title">
                <RotateCcw size={22} strokeWidth={2.2} aria-hidden="true" />
                <h2>Previous Scene Release</h2>
              </div>
              <ReleaseSummary release={sceneStatus.previousRelease} emptyLabel="No rollback release is available." />
              <button type="button" className="admin-secondary-action" disabled={!sceneStatus.previousRelease || isSceneLoading} onClick={handleRollbackScenes}>
                <RotateCcw size={16} aria-hidden="true" /> Rollback
              </button>
            </div>
          </section>

          <section className="admin-panel">
            <div className="admin-panel-title">
              <Upload size={22} strokeWidth={2.2} aria-hidden="true" />
              <h2>Validate Scene ZIP</h2>
            </div>
            <p className="admin-panel-help">Put Scene JSON under a supported language folder such as <code>en/scenes.json</code>, and put Scene-only images under <code>assets/</code>. Reference those images with a lowercase value such as <code>scene:practice/practice_0_0.webp</code>.</p>
            <form className="admin-preset-upload" onSubmit={handleValidateSceneBundle}>
              <input
                type="file"
                accept=".zip,application/zip"
                aria-label="Scene Library ZIP"
                onChange={(event) => {
                  setSceneBundle(event.target.files?.[0] || null);
                  setSceneValidation(null);
                  setScenePublishConfirmation('');
                }}
                disabled={isSceneLoading}
              />
              <button type="submit" disabled={!sceneBundle || isSceneLoading}>{isSceneLoading ? 'Working...' : 'Validate ZIP'}</button>
            </form>
          </section>

          {sceneValidation && (
            <section className="admin-panel admin-validation-panel">
              <div className="admin-panel-title">
                <AlertTriangle size={22} strokeWidth={2.2} aria-hidden="true" />
                <h2>Scene Validation Result</h2>
              </div>
              {sceneValidation.releaseId && <p><strong>Release:</strong> {sceneValidation.releaseId}</p>}
              {sceneValidation.summary && (
                <div className="admin-stat-grid">
                  <span><strong>{sceneValidation.summary.locales}</strong> locales</span>
                  <span><strong>{sceneValidation.summary.scenes}</strong> scenes</span>
                  <span><strong>{sceneValidation.summary.frames}</strong> frames</span>
                  <span><strong>{sceneValidation.summary.triggers}</strong> triggers</span>
                  <span><strong>{sceneValidation.summary.images}</strong> images</span>
                  <span><strong>{formatBytes(sceneValidation.summary.imageBytes)}</strong> image data</span>
                  <span><strong>{sceneValidation.summary.files}</strong> files</span>
                </div>
              )}
              {sceneValidation.changes && (
                <div className="admin-change-grid">
                  <div><strong>Added</strong><small>{sceneValidation.changes.addedSceneIds?.join(', ') || 'None'}</small></div>
                  <div><strong>Removed</strong><small>{sceneValidation.changes.removedSceneIds?.join(', ') || 'None'}</small></div>
                  <div><strong>Retained</strong><small>{sceneValidation.changes.retainedSceneIds?.join(', ') || 'None'}</small></div>
                </div>
              )}
              {!!sceneValidation.errors?.length && (
                <div className="admin-validation-list is-error">
                  <strong>Errors</strong>
                  {sceneValidation.errors.map((item, index) => <small key={`${item.path || item.code}-${index}`}>{item.path ? `${item.path} — ` : ''}{item.message}</small>)}
                </div>
              )}
              {!!sceneValidation.warnings?.length && (
                <div className="admin-validation-list is-warning">
                  <strong>Warnings</strong>
                  {sceneValidation.warnings.map((item, index) => <small key={`${item.path || item.code}-${index}`}>{item.path ? `${item.path} — ` : ''}{item.message}</small>)}
                </div>
              )}
              {sceneValidation.releaseId && !sceneValidation.unchanged && !sceneValidation.errors?.length && (
                <div className="admin-publish-confirmation">
                  <label htmlFor="admin-scene-publish-confirmation">Type <strong>PUBLISH SCENE LIBRARY</strong> to confirm</label>
                  <input
                    id="admin-scene-publish-confirmation"
                    value={scenePublishConfirmation}
                    onChange={(event) => setScenePublishConfirmation(event.target.value)}
                    disabled={isSceneLoading}
                  />
                  <button type="button" className="admin-publish-action" onClick={handlePublishSceneBundle} disabled={isSceneLoading || scenePublishConfirmation !== 'PUBLISH SCENE LIBRARY'}>
                    Publish Scene Library
                  </button>
                </div>
              )}
            </section>
          )}

          <section className="admin-panel">
            <div className="admin-toolbar">
              <div className="admin-panel-title">
                <Search size={22} strokeWidth={2.2} aria-hidden="true" />
                <h2>Scenes</h2>
                <span>{sceneResult.total}</span>
              </div>
              <div className="admin-filters admin-scene-filters">
                <input
                  value={sceneFilters.query}
                  onChange={(event) => setSceneFilters((current) => ({ ...current, query: event.target.value, page: 1 }))}
                  placeholder="Search ID, trigger, actor"
                />
                <select value={sceneFilters.locale} onChange={(event) => setSceneFilters((current) => ({ ...current, locale: event.target.value, page: 1 }))}>
                  <option value="">All languages</option>
                  {SUPPORTED_LANGUAGES.map((language) => <option key={language.code} value={language.code}>{language.label}</option>)}
                </select>
                <select value={sceneFilters.trigger} onChange={(event) => setSceneFilters((current) => ({ ...current, trigger: event.target.value, page: 1 }))}>
                  <option value="">All triggers</option>
                  {sceneResult.triggers.map((item) => <option key={item} value={item}>{item}</option>)}
                </select>
              </div>
            </div>
            <div className="admin-table-wrap">
              <table className="admin-user-table admin-scene-table">
                <thead><tr><th>Language</th><th>ID</th><th>Trigger</th><th>Actor</th><th>Image</th><th>Frames</th></tr></thead>
                <tbody>
                  {sceneResult.scenes.map((scene) => (
                    <tr key={`${scene.locale}:${scene.id}`}>
                      <td>{scene.locale}</td><td>{scene.id}</td><td>{scene.trigger}</td><td>{scene.actor || '-'}</td><td>{scene.image || '-'}</td><td>{scene.frameCount}</td>
                    </tr>
                  ))}
                  {!isSceneLoading && sceneResult.scenes.length === 0 && <tr><td colSpan="6" className="admin-empty">No scenes found.</td></tr>}
                </tbody>
              </table>
            </div>
            <div className="admin-pagination">
              <button type="button" disabled={sceneResult.page <= 1 || isSceneLoading} onClick={() => setSceneFilters((current) => ({ ...current, page: Math.max(1, current.page - 1) }))} aria-label="Previous scene page"><ChevronLeft size={18} /></button>
              <span>{sceneResult.page} / {sceneResult.totalPages}</span>
              <button type="button" disabled={sceneResult.page >= sceneResult.totalPages || isSceneLoading} onClick={() => setSceneFilters((current) => ({ ...current, page: current.page + 1 }))} aria-label="Next scene page"><ChevronRight size={18} /></button>
            </div>
          </section>
        </>
      )}
      {activeTab === 'market' && (
        <AdminMarketPanel
          confirm={confirm}
          onError={handleMarketError}
          onMessage={handleMarketMessage}
        />
      )}
      {confirmDialog}
    </div>
  );
}

export default AdminPage;
